import type {BreakdownItemType, Country, CountryInfo, CurrencyInfo, DestinationGroup, UkIntlCityRate} from './types'

// ── Rate tables ──────────────────────────────────────────────────

export const GERMANY_INTERNATIONAL_RATES: Record<string, {half: number; full: number}> = {
  Austria: {half: 33, full: 50},
  Belgium: {half: 40, full: 59},
  Croatia: {half: 28, full: 42},
  'Czech Republic': {half: 30, full: 44},
  Denmark: {half: 50, full: 75},
  Finland: {half: 36, full: 54},
  'France - Paris': {half: 39, full: 58},
  France: {half: 36, full: 53},
  Germany: {half: 14, full: 28},
  Greece: {half: 34, full: 51},
  Hungary: {half: 28, full: 42},
  Ireland: {half: 40, full: 60},
  'Italy - Rome': {half: 32, full: 48},
  'Italy - Milan': {half: 28, full: 42},
  Italy: {half: 28, full: 42},
  Luxembourg: {half: 42, full: 63},
  Netherlands: {half: 39, full: 58},
  Norway: {half: 46, full: 69},
  Poland: {half: 23, full: 34},
  Portugal: {half: 32, full: 48},
  Romania: {half: 24, full: 36},
  'Spain - Madrid': {half: 28, full: 42},
  Spain: {half: 23, full: 34},
  Sweden: {half: 44, full: 65},
  'Switzerland - Geneva': {half: 47, full: 70},
  Switzerland: {half: 46, full: 68},
  Turkey: {half: 28, full: 41},
  'United Kingdom - London': {half: 44, full: 66},
  'United Kingdom': {half: 35, full: 52},
  'USA - New York': {half: 51, full: 77},
  'USA - San Francisco': {half: 50, full: 74},
  'USA - Other': {half: 45, full: 67},
  Canada: {half: 42, full: 63},
  Brazil: {half: 36, full: 54},
  Japan: {half: 50, full: 74},
  China: {half: 44, full: 66},
  Singapore: {half: 48, full: 72},
  India: {half: 34, full: 51},
  Australia: {half: 44, full: 66},
  UAE: {half: 36, full: 54},
  'Saudi Arabia': {half: 32, full: 48},
  'South Africa': {half: 30, full: 45},
}

export const AUSTRIA_INTERNATIONAL_RATES: Record<string, number> = {
  Belgium: 36,
  Croatia: 25,
  'Czech Republic': 22,
  Denmark: 45,
  Finland: 35,
  'France - Paris': 36,
  France: 33,
  Germany: 26,
  Greece: 30,
  Hungary: 22,
  Ireland: 40,
  'Italy - Rome': 30,
  'Italy - Milan': 28,
  Italy: 26,
  Luxembourg: 40,
  Netherlands: 37,
  Norway: 45,
  Poland: 22,
  Portugal: 30,
  Romania: 22,
  'Spain - Madrid': 30,
  Spain: 25,
  Sweden: 40,
  'Switzerland - Geneva': 43,
  Switzerland: 42,
  Turkey: 26,
  'United Kingdom - London': 40,
  'United Kingdom': 35,
  'USA - New York': 45,
  'USA - San Francisco': 48,
  'USA - Other': 40,
  Canada: 38,
  Brazil: 32,
  Japan: 45,
  China: 40,
  Singapore: 44,
  India: 30,
  Australia: 40,
  UAE: 32,
  'Saudi Arabia': 28,
  'South Africa': 28,
}

export const NETHERLANDS_INTERNATIONAL_RATES: Record<string, number> = {
  Austria: 55,
  Belgium: 60,
  Croatia: 45,
  'Czech Republic': 42,
  Denmark: 72,
  Finland: 56,
  'France - Paris': 62,
  France: 55,
  Germany: 52,
  Greece: 52,
  Hungary: 42,
  Ireland: 65,
  'Italy - Rome': 60,
  'Italy - Milan': 58,
  Italy: 55,
  Luxembourg: 63,
  Netherlands: 39.33,
  Norway: 72,
  Poland: 38,
  Portugal: 52,
  Romania: 40,
  'Spain - Madrid': 52,
  Spain: 47,
  Sweden: 65,
  'Switzerland - Geneva': 76,
  Switzerland: 73,
  Turkey: 42,
  'United Kingdom - London': 75,
  'United Kingdom': 62,
  'USA - New York': 82,
  'USA - San Francisco': 88,
  'USA - Other': 72,
  Canada: 70,
  Brazil: 58,
  Japan: 74,
  China: 65,
  Singapore: 74,
  India: 52,
  Australia: 68,
  UAE: 60,
  'Saudi Arabia': 55,
  'South Africa': 50,
}

// HMRC EIM05255 worldwide subsistence rates (source: gov.uk, city-specific, local currency).
// Accommodation (room) is listed for reference but not added to the allowance — it is reimbursed at actual cost.
export const UK_INTERNATIONAL_CITY_RATES: Record<string, UkIntlCityRate> = {
  Austria: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 89.5,
    rate10h: 84,
    rate5h: 33,
    room: 123.5,
    breakfast: 0,
    lunch: 28.5,
    dinner: 46.5,
  },
  Belgium: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 76,
    rate10h: 71,
    rate5h: 27,
    room: 165.5,
    breakfast: 0,
    lunch: 23,
    dinner: 40,
  },
  'Czech Republic': {
    currency: 'CZK',
    symbol: 'Kč',
    rate24h: 1296.5,
    rate10h: 1153.5,
    rate5h: 455,
    room: 126,
    breakfast: 0,
    lunch: 390,
    dinner: 633.5,
  },
  Denmark: {
    currency: 'DKK',
    symbol: 'kr',
    rate24h: 895.5,
    rate10h: 683,
    rate5h: 235,
    room: 1200,
    breakfast: 164,
    lunch: 213,
    dinner: 426,
  },
  'France - Paris': {
    currency: 'EUR',
    symbol: '€',
    rate24h: 117,
    rate10h: 86.5,
    rate5h: 40,
    room: 199.5,
    breakfast: 24,
    lunch: 35.5,
    dinner: 42,
  },
  France: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 117,
    rate10h: 86.5,
    rate5h: 40,
    room: 199.5,
    breakfast: 24,
    lunch: 35.5,
    dinner: 42,
  },
  Germany: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 72,
    rate10h: 65.5,
    rate5h: 22,
    room: 167.5,
    breakfast: 0,
    lunch: 18.5,
    dinner: 40.5,
  },
  Ireland: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 99,
    rate10h: 94,
    rate5h: 38.5,
    room: 149,
    breakfast: 0,
    lunch: 34,
    dinner: 51.5,
  },
  'Italy - Rome': {
    currency: 'EUR',
    symbol: '€',
    rate24h: 90,
    rate10h: 81.5,
    rate5h: 35.5,
    room: 170,
    breakfast: 0,
    lunch: 31.5,
    dinner: 42.5,
  },
  'Italy - Milan': {
    currency: 'EUR',
    symbol: '€',
    rate24h: 96.5,
    rate10h: 84.5,
    rate5h: 34,
    room: 230,
    breakfast: 0,
    lunch: 31.5,
    dinner: 48,
  },
  Italy: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 90,
    rate10h: 81.5,
    rate5h: 35.5,
    room: 170,
    breakfast: 0,
    lunch: 31.5,
    dinner: 42.5,
  },
  Netherlands: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 72,
    rate10h: 65,
    rate5h: 25,
    room: 150,
    breakfast: 0,
    lunch: 22,
    dinner: 37.5,
  },
  Norway: {
    currency: 'NOK',
    symbol: 'kr',
    rate24h: 848,
    rate10h: 806.5,
    rate5h: 326,
    room: 1192.5,
    breakfast: 0,
    lunch: 296.5,
    dinner: 451,
  },
  Poland: {
    currency: 'PLN',
    symbol: 'zł',
    rate24h: 393,
    rate10h: 372.5,
    rate5h: 154,
    room: 115,
    breakfast: 0,
    lunch: 136,
    dinner: 201,
  },
  Portugal: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 74.5,
    rate10h: 69.5,
    rate5h: 25.5,
    room: 106.5,
    breakfast: 0,
    lunch: 22.5,
    dinner: 41.5,
  },
  'Spain - Madrid': {
    currency: 'EUR',
    symbol: '€',
    rate24h: 114.5,
    rate10h: 86,
    rate5h: 32,
    room: 172.5,
    breakfast: 23,
    lunch: 29.5,
    dinner: 51.5,
  },
  Spain: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 114.5,
    rate10h: 86,
    rate5h: 32,
    room: 172.5,
    breakfast: 23,
    lunch: 29.5,
    dinner: 51.5,
  },
  Sweden: {
    currency: 'SEK',
    symbol: 'kr',
    rate24h: 917.5,
    rate10h: 857.5,
    rate5h: 379,
    room: 1939.5,
    breakfast: 0,
    lunch: 345,
    dinner: 445,
  },
  'Switzerland - Geneva': {
    currency: 'CHF',
    symbol: 'CHF',
    rate24h: 155.5,
    rate10h: 117.5,
    rate5h: 44.6,
    room: 207.5,
    breakfast: 28,
    lunch: 40,
    dinner: 69,
  },
  Switzerland: {
    currency: 'CHF',
    symbol: 'CHF',
    rate24h: 118,
    rate10h: 109.5,
    rate5h: 47,
    room: 184.5,
    breakfast: 0,
    lunch: 43,
    dinner: 59,
  },
  Croatia: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 59,
    rate10h: 54,
    rate5h: 23,
    room: 136,
    breakfast: 0,
    lunch: 18,
    dinner: 30,
  },
  Finland: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 82,
    rate10h: 75.5,
    rate5h: 33,
    room: 146.5,
    breakfast: 0,
    lunch: 25,
    dinner: 41,
  },
  Greece: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 67.5,
    rate10h: 62.5,
    rate5h: 28.5,
    room: 153.5,
    breakfast: 0,
    lunch: 20,
    dinner: 34,
  },
  Hungary: {
    currency: 'HUF',
    symbol: 'Ft',
    rate24h: 16001,
    rate10h: 14512.5,
    rate5h: 6092,
    room: 50000,
    breakfast: 0,
    lunch: 4800,
    dinner: 8000,
  },
  Luxembourg: {
    currency: 'EUR',
    symbol: '€',
    rate24h: 123,
    rate10h: 89,
    rate5h: 33,
    room: 241,
    breakfast: 27,
    lunch: 37,
    dinner: 49,
  },
  Romania: {
    currency: 'RON',
    symbol: 'lei',
    rate24h: 243,
    rate10h: 218.5,
    rate5h: 96,
    room: 730,
    breakfast: 0,
    lunch: 73,
    dinner: 122,
  },
  Turkey: {
    currency: 'TRY',
    symbol: '₺',
    rate24h: 188,
    rate10h: 173.5,
    rate5h: 74,
    room: 5000,
    breakfast: 0,
    lunch: 56,
    dinner: 94,
  },
  'USA - New York': {
    currency: 'USD',
    symbol: '$',
    rate24h: 102.5,
    rate10h: 76.5,
    rate5h: 31,
    room: 216,
    breakfast: 20,
    lunch: 26,
    dinner: 41,
  },
  'USA - San Francisco': {
    currency: 'USD',
    symbol: '$',
    rate24h: 99.5,
    rate10h: 71.5,
    rate5h: 21,
    room: 190.5,
    breakfast: 22,
    lunch: 28,
    dinner: 38,
  },
  'USA - Other': {
    currency: 'USD',
    symbol: '$',
    rate24h: 91.5,
    rate10h: 70.5,
    rate5h: 30.5,
    room: 177,
    breakfast: 17,
    lunch: 21,
    dinner: 38,
  },
  Canada: {
    currency: 'CAD',
    symbol: 'C$',
    rate24h: 156.5,
    rate10h: 119,
    rate5h: 47,
    room: 223.5,
    breakfast: 30,
    lunch: 37.5,
    dinner: 72.5,
  },
  Brazil: {
    currency: 'BRL',
    symbol: 'R$',
    rate24h: 300.5,
    rate10h: 272,
    rate5h: 101.5,
    room: 453,
    breakfast: 0,
    lunch: 90,
    dinner: 150,
  },
  Japan: {
    currency: 'JPY',
    symbol: '¥',
    rate24h: 11290.5,
    rate10h: 10534.5,
    rate5h: 4445.5,
    room: 18381.5,
    breakfast: 0,
    lunch: 3390,
    dinner: 5645,
  },
  China: {
    currency: 'CNY',
    symbol: '¥',
    rate24h: 605.5,
    rate10h: 563,
    rate5h: 232,
    room: 1344.5,
    breakfast: 0,
    lunch: 182,
    dinner: 303,
  },
  Singapore: {
    currency: 'SGD',
    symbol: 'S$',
    rate24h: 218,
    rate10h: 206.5,
    rate5h: 91.5,
    room: 318,
    breakfast: 0,
    lunch: 65,
    dinner: 109,
  },
  India: {
    currency: 'INR',
    symbol: '₹',
    rate24h: 6100.5,
    rate10h: 5572,
    rate5h: 2334,
    room: 13926,
    breakfast: 0,
    lunch: 1830,
    dinner: 3050,
  },
  Australia: {
    currency: 'AUD',
    symbol: 'A$',
    rate24h: 195,
    rate10h: 147.5,
    rate5h: 57.5,
    room: 227,
    breakfast: 38,
    lunch: 47.5,
    dinner: 90,
  },
  UAE: {
    currency: 'AED',
    symbol: 'AED',
    rate24h: 614.5,
    rate10h: 432,
    rate5h: 161,
    room: 949.5,
    breakfast: 143,
    lunch: 182.5,
    dinner: 271,
  },
  'Saudi Arabia': {
    currency: 'SAR',
    symbol: 'SAR',
    rate24h: 484.5,
    rate10h: 344.5,
    rate5h: 135.5,
    room: 674.5,
    breakfast: 111,
    lunch: 145.5,
    dinner: 209,
  },
  'South Africa': {
    currency: 'ZAR',
    symbol: 'R',
    rate24h: 456,
    rate10h: 388.5,
    rate5h: 147,
    room: 2051.5,
    breakfast: 0,
    lunch: 137,
    dinner: 228,
  },
}

export const INTERNATIONAL_DESTINATIONS: DestinationGroup[] = [
  {
    group: 'Europe',
    options: [
      'Austria',
      'Belgium',
      'Croatia',
      'Czech Republic',
      'Denmark',
      'Finland',
      'France - Paris',
      'France',
      'Germany',
      'Greece',
      'Hungary',
      'Ireland',
      'Italy - Rome',
      'Italy - Milan',
      'Italy',
      'Luxembourg',
      'Netherlands',
      'Norway',
      'Poland',
      'Portugal',
      'Romania',
      'Spain - Madrid',
      'Spain',
      'Sweden',
      'Switzerland - Geneva',
      'Switzerland',
      'Turkey',
      'United Kingdom - London',
      'United Kingdom',
    ],
  },
  {group: 'Americas', options: ['USA - New York', 'USA - San Francisco', 'USA - Other', 'Canada', 'Brazil']},
  {group: 'Asia Pacific', options: ['Japan', 'China', 'Singapore', 'India', 'Australia']},
  {group: 'Middle East & Africa', options: ['UAE', 'Saudi Arabia', 'South Africa']},
]

// ── Destination currencies & FX rates ────────────────────────────

export const DESTINATION_CURRENCIES: Record<string, CurrencyInfo> = {
  Austria: {code: 'EUR', symbol: '€'},
  Belgium: {code: 'EUR', symbol: '€'},
  Croatia: {code: 'EUR', symbol: '€'},
  'Czech Republic': {code: 'CZK', symbol: 'Kč'},
  Denmark: {code: 'DKK', symbol: 'kr'},
  Finland: {code: 'EUR', symbol: '€'},
  'France - Paris': {code: 'EUR', symbol: '€'},
  France: {code: 'EUR', symbol: '€'},
  Germany: {code: 'EUR', symbol: '€'},
  Greece: {code: 'EUR', symbol: '€'},
  Hungary: {code: 'HUF', symbol: 'Ft'},
  Ireland: {code: 'EUR', symbol: '€'},
  'Italy - Rome': {code: 'EUR', symbol: '€'},
  'Italy - Milan': {code: 'EUR', symbol: '€'},
  Italy: {code: 'EUR', symbol: '€'},
  Luxembourg: {code: 'EUR', symbol: '€'},
  Netherlands: {code: 'EUR', symbol: '€'},
  Norway: {code: 'NOK', symbol: 'kr'},
  Poland: {code: 'PLN', symbol: 'zł'},
  Portugal: {code: 'EUR', symbol: '€'},
  Romania: {code: 'RON', symbol: 'lei'},
  'Spain - Madrid': {code: 'EUR', symbol: '€'},
  Spain: {code: 'EUR', symbol: '€'},
  Sweden: {code: 'SEK', symbol: 'kr'},
  'Switzerland - Geneva': {code: 'CHF', symbol: 'CHF'},
  Switzerland: {code: 'CHF', symbol: 'CHF'},
  Turkey: {code: 'TRY', symbol: '₺'},
  'United Kingdom - London': {code: 'GBP', symbol: '£'},
  'United Kingdom': {code: 'GBP', symbol: '£'},
  'USA - New York': {code: 'USD', symbol: '$'},
  'USA - San Francisco': {code: 'USD', symbol: '$'},
  'USA - Other': {code: 'USD', symbol: '$'},
  Canada: {code: 'CAD', symbol: 'C$'},
  Brazil: {code: 'BRL', symbol: 'R$'},
  Japan: {code: 'JPY', symbol: '¥'},
  China: {code: 'CNY', symbol: '¥'},
  Singapore: {code: 'SGD', symbol: 'S$'},
  India: {code: 'INR', symbol: '₹'},
  Australia: {code: 'AUD', symbol: 'A$'},
  UAE: {code: 'AED', symbol: 'د.إ'},
  'Saudi Arabia': {code: 'SAR', symbol: '﷼'},
  'South Africa': {code: 'ZAR', symbol: 'R'},
}

// ── UI constants ─────────────────────────────────────────────────

export const COUNTRY_NAMES: Record<Country, string> = {
  uk: 'United Kingdom',
  de: 'Germany',
  at: 'Austria',
  nl: 'Netherlands',
}

export const COUNTRY_INFO: CountryInfo[] = [
  {id: 'uk', flag: '🇬🇧', label: 'UK'},
  {id: 'de', flag: '🇩🇪', label: 'Germany'},
  {id: 'at', flag: '🇦🇹', label: 'Austria'},
  {id: 'nl', flag: '🇳🇱', label: 'Netherlands'},
]

export const BREAKDOWN_DOT_COLORS: Record<BreakdownItemType, string> = {
  pos: '#1a9e5c',
  neg: '#e84040',
  tot: '#222B27',
  zero: '#DCDBDA',
}

export const INPUT_CLASS =
  'w-full appearance-none rounded-lg border border-grey-150 bg-moss-white px-3 py-[11px] text-14 font-normal leading-5 tracking-[.16px] text-moss-black placeholder:text-grey-300 hover:bg-grey-110 focus:outline-none'

export const MEAL_OPTIONS = [
  {key: 'breakfast' as const, label: 'Breakfast included'},
  {key: 'lunch' as const, label: 'Lunch included'},
  {key: 'dinner' as const, label: 'Dinner included'},
] as const

const AVAILABLE_DESTINATIONS: Record<Country, Set<string>> = {
  de: new Set(Object.keys(GERMANY_INTERNATIONAL_RATES)),
  uk: new Set(Object.keys(UK_INTERNATIONAL_CITY_RATES)),
  at: new Set(Object.keys(AUSTRIA_INTERNATIONAL_RATES)),
  nl: new Set(Object.keys(NETHERLANDS_INTERNATIONAL_RATES)),
}

export function getAvailableDestinations(country: Country): Set<string> {
  return AVAILABLE_DESTINATIONS[country]
}
